import React, { useState, useEffect, useRef } from 'react';
import { useNavigation } from 'context/NavigationContext';
import { useParams } from 'react-router-dom';
import { BringzzButton, BringzzIcon, BringzzBottomDrawer, BringzzInput, BringzzOverlay, BringzzText } from '@bringzz/components';
import CameraStream from 'components/CameraStream';
import useRequest from 'hooks/useRequest';
import { useOrder } from 'context/OrderContext';
// Internal CameraControlPanel component
const CameraControlPanel = ({ onSwitchCamera, onImageSelected, isDisabled }) => {
    const fileInputRef = React.useRef(null);

    const handleGalleryClick = () => {
        if (isDisabled) return;
        fileInputRef.current?.click();
    };

    const handleFileChange = (event) => {
        if (isDisabled) return;
        const file = event.target.files?.[0];
        if (file && (file.type === 'image/jpeg' || file.type === 'image/png')) {
            onImageSelected(file);
        }
    };

    const handleCameraClick = () => {
        if (isDisabled) return;
        onImageSelected('capture');
    };

    return (
        <div className="absolute flex flex-col bottom-0 py-6 w-full bg-black/80">
            <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/jpeg,image/png"
                onChange={handleFileChange}
                disabled={isDisabled}
            />
            <div className="flex items-center justify-around">
                <BringzzIcon
                    folder="LineIcons"
                    icon="IconGallery"
                    size="28"
                    className={`text-white cursor-pointer ${isDisabled ? 'opacity-50' : ''}`}
                    onClick={handleGalleryClick}
                />
                <div className={`relative border rounded-full p-1 cursor-pointer ${isDisabled ? 'opacity-50' : ''}`}>
                    <div
                        className="h-14 w-14 bg-white rounded-full"
                        onClick={handleCameraClick}
                    />
                    {isDisabled && (
                        <>
                            <div className="absolute top-0 left-1/2 h-full w-[2px] bg-black/20 -translate-x-1/2 animate-spin" />
                            <div className="absolute top-1/2 left-0 w-full h-[2px] bg-black/20 -translate-y-1/2 animate-spin" />
                        </>
                    )}
                </div>
                <BringzzIcon
                    folder="LineIcons"
                    icon="IconSwitch"
                    size="24"
                    className={`text-white cursor-pointer ${isDisabled ? 'opacity-50' : ''}`}
                    onClick={() => !isDisabled && onSwitchCamera()}
                />
            </div>
        </div>
    );
};

// Internal CameraModeOverlay component
const CameraModeOverlay = ({ previewImage }) => {
    return (
        <>
            {/* Camera frame overlay when taking a picture */}
            {/* {!previewImage && (
                <div className="absolute top-1/2 left-1/2 w-64 h-64 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none grid grid-cols-3 grid-rows-3">
                    <div className="border-t-2 border-l-2 border-white bg-opacity-50 col-start-1 row-start-1" />
                    <div className="border-t-2 border-r-2 border-white bg-opacity-50 col-start-3 row-start-1" />
                    <div className="border-b-2 border-l-2 border-white bg-opacity-50 col-start-1 row-start-3" />
                    <div className="border-b-2 border-r-2 border-white bg-opacity-50 col-start-3 row-start-3" />
                </div>
            )} */}

            {/* Instructions text */}
            <div className="absolute top-32 text-center w-full px-8">
                <BringzzText tag="h4" className="bg-black/60 p-2 text-white font-normal">
                    {!previewImage
                        ? 'Take a picture or select from gallery'
                        : 'Review your image'
                    }
                </BringzzText>
            </div>
        </>
    );
};

// Main Camera Page Component
const CourierPage = () => {
    const { backPress, navigate } = useNavigation();
    const { mode, type } = useParams();
    const [selectedMode] = useState(mode ? mode : 'handover');
    const [detectedText, setDetectedText] = useState('');
    const cameraStreamRef = useRef();
    const { sendRequest, loading } = useRequest();
    const [isUploading, setIsUploading] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [showCourierDrawer, setShowCourierDrawer] = useState(false);
    const [courierName, setCourierName] = useState('');
    const [trackingNumber, setTrackingNumber] = useState('');
    const [showImageActions, setShowImageActions] = useState(false);
    const { orderDetails, proceedToNextStep, activeStep } = useOrder();

    const handleBackPress = () => {
        if (cameraStreamRef.current) {
            cameraStreamRef.current.stopCamera();
        }
        backPress();
    };

    const handleSwitchCamera = () => {
        if (cameraStreamRef.current) {
            cameraStreamRef.current.switchCamera();
        }
    };

    const handleDetectValue = (result) => {
        console.log(result.text);
        setDetectedText(result.text);
    };

    const handleImageSelected = async (input) => {
        if (input === 'capture') {
            if (cameraStreamRef.current) {
                const imageBlob = await cameraStreamRef.current.takePhoto();
                if (imageBlob) {
                    const previewUrl = URL.createObjectURL(imageBlob);
                    setPreviewImage(previewUrl);
                    setShowImageActions(true);
                }
            }
        } else {
            const previewUrl = URL.createObjectURL(input);
            setPreviewImage(previewUrl);
            setShowImageActions(true);
        }
    };

    const resetCamera = () => {
        setIsUploading(false);
        setPreviewImage(null);
        if (cameraStreamRef.current) {
            cameraStreamRef.current.resetCamera();
        }
    };

    const handleRetake = () => {
        resetCamera();
        setShowImageActions(false);
    };

    const handleUseImage = () => {
        setShowCourierDrawer(true);
    };

    const handleCourierSubmit = async () => {
        console.log('Courier submission with:', {
            courierName,
            trackingNumber,
            image: previewImage
        });
        // Add your submission logic here

        setShowCourierDrawer(false);
        await sendRequest({
            method: 'POST',
            url: `/module/order/courierDispatched`,
            data: {
                "orderUUID": orderDetails.integrationId,
            }
        }).then((response) => {
            if (response.status === 'success') {
                proceedToNextStep()
                navigate('/matchmaking/pharmacy/2');
            } else {
                console.log("orderIsReady error");
            }
        }).catch((error) => {
            console.error(error);
        })
    };

    return (
        <div className="fixed inset-0">
            {/* Show either preview image or camera stream */}
            {previewImage ? (
                <div className="h-full w-full">
                    <img
                        src={previewImage}
                        alt="Preview"
                        className="h-full w-full object-cover"
                    />
                </div>
            ) : (
                <CameraStream
                    ref={cameraStreamRef}
                    setDetectedText={handleDetectValue}
                />
            )}

            {/* Camera Mode Overlay */}
            <CameraModeOverlay previewImage={previewImage} />

            {/* Loading Overlay */}
            {isUploading && (
                <BringzzOverlay>
                    <div className="flex flex-col items-center justify-center h-full">
                        <div className="animate-spin mb-4">
                            <BringzzIcon
                                size="32"
                                icon="IconSpinner"
                                folder="LineIcons"
                                className="text-white"
                            />
                        </div>
                        <BringzzText tag="h3" className="text-white">
                            Processing your image...
                        </BringzzText>
                    </div>
                </BringzzOverlay>
            )}

            {/* Top-left content area */}
            <div className="absolute top-0 left-0 p-5 flex justify-between w-full">
                <BringzzIcon
                    size="16"
                    icon="IconClose"
                    folder="LineIcons"
                    className="cursor-pointer text-white bg-black/80 rounded-full p-3"
                    onClick={handleBackPress}
                />
                <BringzzIcon
                    size="24"
                    icon="IconThunderbolt"
                    folder="LineIcons"
                    className="cursor-pointer text-white bg-black/80 rounded-full p-2"
                    onClick={() => { }}
                />
            </div>

            {/* Bottom control area */}
            {!showImageActions ? (
                <CameraControlPanel
                    onImageSelected={handleImageSelected}
                    onSwitchCamera={handleSwitchCamera}
                    isDisabled={isUploading}
                />
            ) : (
                <div className="absolute bottom-0 left-0 right-0 p-4 flex justify-center gap-4">
                    <BringzzButton
                        onClick={handleRetake}
                        className="bg-gray-500 text-white"
                    >
                        Retake
                    </BringzzButton>
                    <BringzzButton
                        onClick={handleUseImage}
                        className="bg-magic-lilac"
                    >
                        Use Photo
                    </BringzzButton>
                </div>
            )}

            {/* Courier Info Drawer */}
            <BringzzBottomDrawer
                isOpen={showCourierDrawer}
                close={() => setShowCourierDrawer(false)}
            >
                <div className="p-4 flex flex-col">
                    <div className='flex justify-center items-center'>
                        <BringzzIcon
                            size="16"
                            icon="IconCheck"
                            folder="CustomIcons"
                            className="cursor-pointer bg-magic-lilac rounded-full p-3"
                        />
                    </div>
                    <BringzzText tag='h2' className='p-2 font-normal text-center'>Receipt scanned!</BringzzText>
                    <BringzzText tag='h4' className='p-2 font-normal text-center'>Eget nibh libero viverra pulvinar nunc tellus velit aenean enim. Sagittis eget sagittis ullamcorper tristique. Laoreet volutpat gravida mollis.</BringzzText>
                    <div className='gap-4'>
                        <BringzzInput
                            label="Courier Name"
                            placeholder="Enter courier name"
                            value={courierName}
                            onChange={(e, newIsValid, newValue) => setCourierName(newValue)}
                        />
                        <BringzzInput
                            label="Tracking Number"
                            placeholder="Enter tracking number"
                            value={trackingNumber}
                            onChange={(e, newIsValid, newValue) => setTrackingNumber(newValue)}
                        />
                    </div>

                </div>
                <div className="text-center mb-12 px-4">
                    <BringzzButton
                        className="bg-magic-lilac w-full flex justify-center"
                        onClick={handleCourierSubmit}
                        disabled={!courierName || !trackingNumber}
                    >
                        Continue
                    </BringzzButton>
                </div>
            </BringzzBottomDrawer>
        </div>
    );
};

export default CourierPage;
